import { CField } from '@/_components/CField';
import CPhoneInput from '@/_components/PhoneInput';
import customerService from '@/_service/customer.service';
import { authActions } from '@/_store/auth/auth.slice';
import { IRedux } from '@/_store/types';
import { FEATURED_BRANDS } from '@/_utils/Constants';
import { Button } from '@/components/ui/button';
import {
  DialogCloseTrigger,
  DialogContent,
  DialogRoot,
} from '@/components/ui/dialog';
import { toaster } from '@/components/ui/toaster';
import { Box, Flex, Input, Text } from '@chakra-ui/react';
import { useTranslations } from 'next-intl';
import React, { Dispatch, FormEvent, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const Desktop = ({
  isOpen,
  setIsOpen,
}: {
  isOpen: boolean;
  setIsOpen: Dispatch<boolean>;
}) => {
  const { user } = useSelector((state: IRedux) => state.auth);
  const dispatch = useDispatch();
  const t = useTranslations();

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    last_name: '',
    date_of_birth: '',
  });

  // Update formData when user data changes
  useEffect(() => {
    if (user) {
      const nameParts = user.name?.split(' ') || ['', ''];
      setFormData({
        name: nameParts[0] || '',
        phone: user.phone || '',
        last_name: nameParts[1] || '',
        date_of_birth: user.date_of_birth || '',
      });
    }
  }, [user]);

  const { name, phone, last_name, date_of_birth } = formData;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!user?.id) {
      toaster.error({
        title: 'Error',
        description: 'User ID is missing',
      });
      return;
    }

    const data = {
      phone: phone,
      date_of_birth: date_of_birth || null,
      name: `${name} ${last_name}`.trim(),
    };

    try {
      await customerService.update(user.id, data);
      dispatch(
        authActions.updateUser({
          name: data.name,
          date_of_birth: date_of_birth,
          shipperId: FEATURED_BRANDS['yaponamama'],
        })
      );
      toaster.success({
        title: t('successfully_edited'),
      });
      setIsOpen(false);
    } catch (error: any) {
      toaster.error({
        title: 'Failed to update user data',
        description: error?.data?.Error?.message,
      });
    }
  };

  return (
    <DialogRoot
      placement={'center'}
      lazyMount
      open={isOpen}
      onOpenChange={(e) => setIsOpen(e.open)}
    >
      <DialogContent p={4} py={6}>
        <Box>
          <Text color={'text'} fontSize={24} fontWeight={700}>
            {t('edit_data')}
          </Text>

          <form onSubmit={onSubmit}>
            <Flex flexDirection={'column'} gap={4} mt={4}>
              <CField label={t('name')} bgColor='background' color={'text'}>
                <Input
                  type='text'
                  className='peer'
                  placeholder=''
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prevData) => ({
                      ...prevData,
                      name: e.target.value,
                    }))
                  }
                />
              </CField>
              <CField label={t('birthday')} bgColor='background' color={'text'}>
                <Input
                  type='date'
                  className='peer'
                  placeholder=''
                  value={formData.date_of_birth}
                  onChange={(e) =>
                    setFormData((prevData) => ({
                      ...prevData,
                      date_of_birth: e.target.value,
                    }))
                  }
                />
              </CField>
              <CField readOnly bgColor='background' color={'text'}>
                <CPhoneInput
                  bgColor='background'
                  color={'text'}
                  label={t('phone_number')}
                  value={formData.phone}
                  onChange={(e) =>
                    typeof e === 'string' &&
                    setFormData((prevData) => ({
                      ...prevData,
                      phone: e,
                    }))
                  }
                />
              </CField>
              <Button
                height={'56px'}
                width={'full'}
                alignSelf={'end'}
                bg='primary'
                color='primary.text'
                _hover={{ bg: 'primary.hover' }}
                mt={'8px'}
                rounded={16}
                type='submit'
                size={'xl'}
              >
                {t('save')}
              </Button>
            </Flex>
          </form>
        </Box>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  );
};

export default Desktop;
